import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiHome, FiUsers, FiPackage, FiFileText, FiCreditCard,
  FiSettings, FiLogOut, FiMenu, FiX, FiChevronDown, FiBell, FiFile
} from 'react-icons/fi';
import { settingsAPI, dashboardAPI } from '../utils/api';
import './Layout.css';

const Logo = () => (
  <div className="logo-wrapper">
    <img
      src="/logo.svg"
      alt="iOnline ERP"
      className="logo-image"
    />
    <span className="logo-text">iOnline ERP</span>
  </div>
);

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [overdueCount, setOverdueCount] = useState(0);

  useEffect(() => {
    const fetchCompanyLogo = async () => {
      try {
        const response = await settingsAPI.getAll();
        if (response.data.settings?.company_logo?.value) {
          // Use relative path directly - works in both development and production
          setCompanyLogo(response.data.settings.company_logo.value);
        }
      } catch (error) {
        console.error('Failed to fetch company logo:', error);
      }
    };
    fetchCompanyLogo();
  }, []);

  // Notification badge = real overdue-invoice count (0 hides the badge)
  useEffect(() => {
    const fetchOverdueCount = async () => {
      try {
        const response = await dashboardAPI.getOverdue();
        setOverdueCount((response.data.invoices || []).length);
      } catch (error) {
        setOverdueCount(0);
      }
    };
    fetchOverdueCount();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navSections = [
    {
      label: null,
      items: [{ path: '/dashboard', icon: FiHome, label: 'Dashboard' }],
    },
    {
      label: 'Manage',
      items: [
        { path: '/clients', icon: FiUsers, label: 'Clients' },
        { path: '/items', icon: FiPackage, label: 'Items' },
      ],
    },
    {
      label: 'Sales',
      items: [
        { path: '/invoices', icon: FiFileText, label: 'Invoices' },
        { path: '/quotations', icon: FiFile, label: 'Quotations' },
        { path: '/payments', icon: FiCreditCard, label: 'Payments' },
      ],
    },
    {
      label: 'System',
      items: [{ path: '/settings', icon: FiSettings, label: 'Settings' }],
    },
  ];

  return (
    <div className="app-container">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            {companyLogo ? (
              <div className="company-logo-wrapper">
                <img
                  src={companyLogo}
                  alt="Company Logo"
                  className="company-logo-image"
                  onError={(e) => {
                    e.target.src = '/logo.svg'; // Fallback to default logo
                  }}
                />
              </div>
            ) : (
              <Logo />
            )}
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <FiX />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navSections.map((section, i) => (
            <div className="nav-group" key={section.label || `nav-group-${i}`}>
              {section.label && (
                <div className="nav-group-label">{section.label}</div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="nav-icon" />
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <Logo />
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-wrapper">
        {/* Header */}
        <header className="header">
          <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>
            <FiMenu />
          </button>

          <div className="header-title">
            <h1>iOnline ERP</h1>
          </div>

          <div className="header-actions">
            <button
              className="header-btn notification-btn"
              title={overdueCount > 0 ? `${overdueCount} overdue invoice(s)` : 'No overdue invoices'}
              onClick={() => navigate('/invoices?status=overdue')}
            >
              <FiBell />
              {overdueCount > 0 && (
                <span className="notification-badge">{overdueCount}</span>
              )}
            </button>

            <div className="user-menu">
              <button 
                className="user-menu-toggle"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="user-avatar small">
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </div>
                <span className="user-name-desktop">{user?.firstName}</span>
                <FiChevronDown className="chevron" />
              </button>

              {userMenuOpen && (
                <div className="dropdown-menu show">
                  <div className="dropdown-item user-info-dropdown">
                    <strong>{user?.firstName} {user?.lastName}</strong>
                    <span className="text-muted">{user?.email}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <NavLink to="/settings" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                    <FiSettings /> Settings
                  </NavLink>
                  <button className="dropdown-item danger" onClick={handleLogout}>
                    <FiLogOut /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="main-content">
          <Outlet />
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default Layout;